import { CSSObjectWithLabel, OptionsOrGroups, PropsValue } from "react-select";
import AsyncSelect from "react-select/async";
import DefaultSelect from "react-select";
import Select from "react-select/dist/declarations/src/Select";
import ErrorHandler from "../../ErrorHandler/ErrorHandler.component";
import { useCallback, useRef, useState } from "react";

import styles from "../CustomSelect.module.scss";
import "./customSearchDropdown.css";

interface ICustomSearchDropdown {
  label?: string;
  onChange: any;
  iconType?: string;
  loadOptions?: (arg: string) => Promise<any>;
  register?:
    | React.Ref<
        Select<
          | string
          | {
              label: string;
              value: string;
            },
          boolean,
          any
        >
      >
    | undefined;
  name?: string;
  isShowLabel?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  defaultOptions?: boolean | OptionsOrGroups<string, any> | undefined;
  labelClass?: string;
  defaultValue?:
    | PropsValue<
        | string
        | {
            label: string;
            value: string;
          }
      >
    | undefined;
  disabled?: boolean;
  placeholder?: string;
  inputHeight?: number;
  labelStyle?: object;
  inputWidth?: number;
  errors?: object;
  debounceTime?: number;
  options?:
    | OptionsOrGroups<
        | string
        | {
            label: string;
            value: string;
          },
        any
      >
    | undefined;
  isAsync?: boolean;
  isLoading?: boolean;
}

const CustomSearchDropDown = ({
  label,
  onChange,
  iconType,
  loadOptions,
  register,
  name,
  isShowLabel,
  isMulti,
  isSearchable,
  defaultOptions,
  labelClass,
  defaultValue,
  disabled,
  placeholder,
  inputHeight,
  labelStyle,
  inputWidth,
  errors,
  options,
  isAsync = true,
  debounceTime = 500,
  isLoading,
}: ICustomSearchDropdown) => {
  const customStyles: any = {
    control: (base: CSSObjectWithLabel, state: any) => ({
      ...base,
      minHeight: inputHeight,
      minWidth: inputWidth,
      borderColor: state?.isFocused ? "#713fff" : "hsl(0, 0%, 80%)",
      borderRadius: "10px",
    }),
  };

  // Create a debounce timer ref
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Implement debounced search with correct type signature
  const debouncedLoadOptions = useCallback(
    (
      inputValue: string,
      callback: (
        options: OptionsOrGroups<string | { label: string; value: string }, any>
      ) => void
    ) => {
      // Clear previous timer
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Set new timer
      debounceTimerRef.current = setTimeout(() => {
        if (loadOptions) {
          loadOptions(inputValue)
            .then((result) => {
              callback(result);
            })
            .catch(() => {
              callback([]);
            });
        } else {
          callback([]);
        }
      }, debounceTime);
    },
    [loadOptions, debounceTime]
  );

  return (
    <div className={`form-group ${styles.customSelect}`}>
      {isShowLabel && (
        <label
          className={`label-txt form-label d-flex justify-content-between ${labelClass}`}
          style={{ ...labelStyle }}
        >
          {iconType ? <i className={iconType} /> : ""}
          {label}
        </label>
      )}
      {isAsync ? (
        <AsyncSelect
          placeholder={placeholder}
          defaultValue={defaultValue}
          isClearable
          isSearchable={isSearchable}
          loadOptions={debouncedLoadOptions}
          defaultOptions={defaultOptions}
          onChange={onChange}
          ref={register}
          name={name}
          isMulti={isMulti}
          isDisabled={disabled}
          styles={customStyles}
          cacheOptions
          isLoading={isLoading}
        />
      ) : (
        <DefaultSelect
          placeholder={placeholder}
          defaultValue={defaultValue}
          isClearable
          isSearchable={isSearchable}
          options={options}
          onChange={onChange}
          ref={register}
          name={name}
          isMulti={isMulti}
          isDisabled={disabled}
          styles={customStyles}
          isLoading={isLoading}
        />
      )}

      <ErrorHandler errors={errors} />
    </div>
  );
};

export default CustomSearchDropDown;
